import { useState } from 'react';
import { Button } from '@/components/Button';
import { MdAdd, MdMoreVert } from 'react-icons/md';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import type { AgentApiAllianceAgentsItem, AgentUserStatus } from '@/types/serializers';
import useCreateAllianceAgent from '../api/useCreateAllianceAgent';
import useDeleteAllianceAgent from '../api/useDeleteAllianceAgent';
import useFetchAllianceAgents from '../api/useFetchAllianceAgents';
import useFetchAllianceAgentsMetadata from '../api/useFetchAllianceAgentsMetadata';
import useResendInvitation from '../api/useResendInvitation';
import useUpdateAllianceAgent from '../api/useUpdateAllianceAgent';
import useFetchInvitationUrl from '../api/useFetchInvitationUrl';
import Loading from '@/components/Loading';
import { getErrorMessage } from '../utils/getErrorMessage';
import InviteAgentModal from './InviteAgentModal';
import EditAgentModal from './EditAgentModal';
import DeleteAgentModal from './DeleteAgentModal';
import InviteUrlModal from './InviteUrlModal';

export default function AgentManagement() {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInviteUrlModalOpen, setIsInviteUrlModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentApiAllianceAgentsItem | null>(null);
  const [inviteUrlAgentId, setInviteUrlAgentId] = useState<number | null>(null);
  const [actionErrorMessage, setActionErrorMessage] = useState<string | null>(null);

  const {
    data: allianceAgents,
    isLoading: isAgentsLoading,
    error: agentsError,
  } = useFetchAllianceAgents();
  const {
    data: allianceAgentsMetadata,
    isLoading: isMetadataLoading,
    error: metadataError,
  } = useFetchAllianceAgentsMetadata();
  const createAllianceAgent = useCreateAllianceAgent();
  const updateAllianceAgent = useUpdateAllianceAgent();
  const deleteAllianceAgent = useDeleteAllianceAgent();
  const resendInvitation = useResendInvitation();
  const {
    data: invitationUrlData,
    isLoading: isInvitationUrlLoading,
    error: invitationUrlError,
  } = useFetchInvitationUrl(inviteUrlAgentId);

  const agents = allianceAgents?.records ?? [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  };

  const fetchErrorMessage = agentsError
    ? getErrorMessage(agentsError, 'エージェント一覧の取得に失敗しました')
    : metadataError
      ? getErrorMessage(metadataError, 'エージェント上限情報の取得に失敗しました')
      : null;
  const isAnyModalOpen =
    isInviteModalOpen || isEditModalOpen || isDeleteModalOpen || isInviteUrlModalOpen;
  const errorMessage = (isAnyModalOpen ? null : actionErrorMessage) || fetchErrorMessage;
  const isLoading = isAgentsLoading || isMetadataLoading;

  const handleInviteAgent = (data: { name: string; email: string }) => {
    setActionErrorMessage(null);
    createAllianceAgent.mutate(data, {
      onSuccess: () => {
        setIsInviteModalOpen(false);
        showSuccessToast('招待メールを送信しました');
      },
      onError: (error) => {
        setActionErrorMessage(getErrorMessage(error, 'エージェントの招待に失敗しました'));
      },
    });
  };

  const handleEditAgent = (agent: AgentApiAllianceAgentsItem) => {
    setSelectedAgent(agent);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = (data: { name: string; email: string }) => {
    if (!selectedAgent) return;
    setActionErrorMessage(null);
    updateAllianceAgent.mutate(
      { id: selectedAgent.id, name: data.name, email: data.email },
      {
        onSuccess: () => {
          setIsEditModalOpen(false);
          setSelectedAgent(null);
          showSuccessToast(`${data.name}を保存しました`);
        },
        onError: (error) => {
          setActionErrorMessage(getErrorMessage(error, 'エージェント情報の更新に失敗しました'));
        },
      }
    );
  };

  const handleDeleteAgent = (agent: AgentApiAllianceAgentsItem) => {
    setSelectedAgent(agent);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedAgent) return;
    const deletingAgentName = selectedAgent.name;
    setActionErrorMessage(null);
    deleteAllianceAgent.mutate(
      { id: selectedAgent.id },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          setSelectedAgent(null);
          showSuccessToast(`${deletingAgentName}を削除しました`);
        },
        onError: (error) => {
          setActionErrorMessage(getErrorMessage(error, 'エージェントの削除に失敗しました'));
        },
      }
    );
  };

  const handleResendInvitation = (agent: AgentApiAllianceAgentsItem) => {
    setActionErrorMessage(null);
    resendInvitation.mutate(
      { id: agent.id },
      {
        onSuccess: () => {
          showSuccessToast(`${agent.name}に招待メールを再送しました`);
        },
        onError: (error) => {
          setActionErrorMessage(getErrorMessage(error, '招待メールの再送に失敗しました'));
        },
      }
    );
  };

  const handleShowInvitationUrl = (agent: AgentApiAllianceAgentsItem) => {
    setSelectedAgent(agent);
    setInviteUrlAgentId(agent.id);
    setActionErrorMessage(null);
    setIsInviteUrlModalOpen(true);
  };

  const handleDisableAgent = (agent: AgentApiAllianceAgentsItem) => {
    setActionErrorMessage(null);
    updateAllianceAgent.mutate(
      { id: agent.id, status: 'inactive' },
      {
        onSuccess: () => {
          showSuccessToast(`${agent.name}を無効化しました`);
        },
        onError: (error) => {
          setActionErrorMessage(getErrorMessage(error, 'エージェントの無効化に失敗しました'));
        },
      }
    );
  };

  const getStatusBadge = (status: AgentUserStatus) => {
    if (status === 'invited') {
      return (
        <span className="flex h-6 w-24 items-center justify-center rounded-full bg-tag-orange px-sm text-tag-sm text-tag-orange">
          招待中
        </span>
      );
    }
    return (
      <span
        className={`flex h-6 w-24 items-center justify-center rounded-full px-sm text-tag-sm ${
          status === 'active' ? 'bg-tag-green text-tag-green' : 'bg-tag-gray text-tag-gray'
        }`}
      >
        {status === 'active' ? 'アクティブ' : '無効'}
      </span>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-md">
      {errorMessage && (
        <div className="rounded border border-solid border-danger bg-red-50 p-sm">
          <p className="text-body-sm text-danger">{errorMessage}</p>
        </div>
      )}

      {/* ヘッダー */}
      <div className="flex items-end justify-between">
        <Button
          color="primary"
          size="md"
          onClick={() => setIsInviteModalOpen(true)}
          disabled={
            !!allianceAgentsMetadata &&
            allianceAgentsMetadata.current_count >= allianceAgentsMetadata.max_agents
          }
        >
          <MdAdd className="size-5" />
          新規エージェントを招待
        </Button>
        <p className="text-body-sm text-sub">
          {allianceAgentsMetadata
            ? `${allianceAgentsMetadata.current_count}/${allianceAgentsMetadata.max_agents}アカウント使用中`
            : '--/--アカウント使用中'}
        </p>
      </div>

      {/* エージェントリスト */}
      <div className="flex flex-col overflow-hidden rounded shadow-layer1">
        {/* テーブルヘッダー */}
        <div className="flex items-center gap-md bg-gray_darken px-md py-xs">
          <div className="w-[350px]">
            <h3 className="text-heading-3xs font-bold text-main">エージェント名</h3>
          </div>
          <div className="w-[280px]">
            <h3 className="text-heading-3xs font-bold text-main">メールアドレス</h3>
          </div>
          <div className="w-[164px]">
            <h3 className="text-heading-3xs font-bold text-main">ステータス</h3>
          </div>
          <div className="flex-1">
            <h3 className="text-heading-3xs font-bold text-main">登録日</h3>
          </div>
          <div className="w-8" />
        </div>

        {/* テーブルボディ */}
        {!agentsError && !metadataError && agents.length === 0 ? (
          <div className="flex items-center justify-center bg-white px-md py-lg">
            <p className="text-body-sm text-sub">エージェントが登録されていません</p>
          </div>
        ) : (
          agents.map((agent, index) => (
            <div
              key={agent.id}
              className={`flex items-center gap-md bg-white px-md py-xs ${
                index === agents.length - 1 ? '' : 'border-b border-solid border-border'
              }`}
            >
              <div className="w-[350px]">
                <p className="text-body-sm">{agent.name}</p>
              </div>
              <div className="w-[280px]">
                <p className="text-body-sm">{agent.email}</p>
              </div>
              <div className="flex w-[164px] flex-col gap-2">{getStatusBadge(agent.status)}</div>
              <div className="flex-1">
                <p className="text-body-sm">{formatDate(agent.created_at)}</p>
              </div>
              <div className="w-8">
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex size-8 items-center justify-center rounded border border-solid border-border bg-white transition-colors hover:bg-gray_darken"
                      title="メニュー"
                    >
                      <MdMoreVert className="size-4 text-main" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-40.75 cursor-pointer rounded bg-white shadow-layer2"
                  >
                    <DropdownMenuItem
                      className="px-sm py-xs text-body-sm hover:opacity-50"
                      onClick={() => handleEditAgent(agent)}
                    >
                      編集
                    </DropdownMenuItem>
                    {agent.status === 'invited' && (
                      <>
                        <DropdownMenuItem
                          className="px-sm py-xs text-body-sm hover:opacity-50"
                          onClick={() => handleShowInvitationUrl(agent)}
                        >
                          招待URLを確認
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="px-sm py-xs text-body-sm hover:opacity-50"
                          onClick={() => handleResendInvitation(agent)}
                        >
                          招待メール再送
                        </DropdownMenuItem>
                      </>
                    )}
                    {agent.status === 'active' && (
                      <DropdownMenuItem
                        className="px-sm py-xs text-body-sm hover:opacity-50"
                        onClick={() => handleDisableAgent(agent)}
                      >
                        無効化
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleDeleteAgent(agent)}
                      className="px-sm py-xs text-body-sm text-danger hover:opacity-50"
                    >
                      削除
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 招待モーダル */}
      <InviteAgentModal
        open={isInviteModalOpen}
        onClose={() => {
          setIsInviteModalOpen(false);
          setActionErrorMessage(null);
        }}
        onInvite={handleInviteAgent}
        isSubmitting={createAllianceAgent.isPending}
        errorMessage={isInviteModalOpen ? actionErrorMessage : null}
      />

      {/* 編集モーダル */}
      <EditAgentModal
        open={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedAgent(null);
          setActionErrorMessage(null);
        }}
        onSave={handleSaveEdit}
        agent={selectedAgent}
        isSubmitting={updateAllianceAgent.isPending}
        errorMessage={isEditModalOpen ? actionErrorMessage : null}
      />

      {/* 招待URLモーダル */}
      <InviteUrlModal
        open={isInviteUrlModalOpen}
        onClose={() => {
          setIsInviteUrlModalOpen(false);
          setSelectedAgent(null);
          setInviteUrlAgentId(null);
          setActionErrorMessage(null);
        }}
        invitationUrl={invitationUrlData?.invitation_url ?? null}
        isLoading={isInvitationUrlLoading}
        errorMessage={
          isInviteUrlModalOpen
            ? invitationUrlError
              ? getErrorMessage(invitationUrlError, '招待URLの取得に失敗しました')
              : actionErrorMessage
            : null
        }
      />

      {/* 削除モーダル */}
      <DeleteAgentModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedAgent(null);
          setActionErrorMessage(null);
        }}
        onDelete={handleConfirmDelete}
        agentName={selectedAgent?.name || ''}
        isSubmitting={deleteAllianceAgent.isPending}
        errorMessage={isDeleteModalOpen ? actionErrorMessage : null}
      />
    </div>
  );
}
